'use client';

import React, { memo } from 'react';
import {
  useVariantSelection,
  ResponsiveImagePath,
} from './VariantSelectionContext';
import VerticalMarquee from './VerticalMarquee';
import { Body2 } from './styleConstants';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { PHONE_MAX_WIDTH_IN_PX } from 'components/theme';
import styles from './VariantHeroSection.module.css';

interface ResponsiveImageProps {
  imagePath: ResponsiveImagePath;
  alt: string;
}

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  imagePath,
  alt,
}) => (
  <picture className={styles.styledPicture}>
    <source
      media={`(max-width: ${PHONE_MAX_WIDTH_IN_PX}px)`}
      srcSet={getCompressedAssetUrl(imagePath.mobile)}
    />
    <img
      className={styles.styledImage}
      src={getCompressedAssetUrl(imagePath.web)}
      alt={alt}
    />
  </picture>
);

const VariantHeroSection: React.FC = memo(() => {
  const { selectedVariant, variantData } = useVariantSelection();

  return (
    <section className={styles.heroSection}>
      <div className={styles.backgroundImageWrapper}>
        <div
          className={styles.imageHalf}
          data-is-left="true"
          data-is-first-on-mobile="false"
        >
          <ResponsiveImage
            imagePath={variantData.leftImagePath}
            alt={`${selectedVariant} variant hero background left`}
          />
        </div>
        <div className={styles.mobileTextSection}>
          <p className={styles.mobileText}>{variantData.slantedText}</p>
        </div>
        <div
          className={styles.imageHalf}
          data-is-left="false"
          data-is-first-on-mobile="true"
        >
          <ResponsiveImage
            imagePath={variantData.rightImagePath}
            alt={`${selectedVariant} variant hero background right`}
          />
          <div className={styles.mobileMarqueeWrapper}>
            <VerticalMarquee text={selectedVariant} />
          </div>
        </div>
      </div>

      <div className={styles.slantedTextOverlay}>
        <Body2 className={styles.slantedText} $color="d_red">
          {variantData.slantedText}
        </Body2>
      </div>

      {/* Desktop marquee - only shows on desktop */}
      <div className={styles.desktopMarqueeWrapper}>
        <VerticalMarquee text={selectedVariant} />
      </div>
    </section>
  );
});

VariantHeroSection.displayName = 'VariantHeroSection';

export default VariantHeroSection;
